/**
 * Presentation for the reusable beat close fade: the two fades, and nothing else.
 *
 * Beat 8's return had its own copy with the cue ids baked in
 * (`BlackoutReturnDirector`) until session 83, when that part became a module
 * and this took its place. This one takes its cue ids as an argument because
 * beat 9 has **two** parts that each need exactly this and nothing more — a
 * puzzle shed and a rest shed — and beat 10 was the third user. A third and
 * fourth copy of twenty lines is where `FadeOverlay` itself came from (see its
 * header: two copies are a coincidence, three are a pattern).
 *
 * **It has no `framing()` and must not grow one.** A director that returns a
 * camera silently beats `shot()` on every render frame, which `docs/traps.md`
 * records as having cost three cameras in one session — including this beat's
 * own review frames.
 */

import { FadeOverlay } from '@/world/fadeOverlay';

export interface BeatFadeCues {
  /** Reveal the room. Emitted by the part's first stage on entry. */
  readonly open: string;
  /** Take it back to black. Emitted by the closing stage. */
  readonly fadeOut: string;
  /** A cue that CUTS to black rather than fading. Optional; beat 14's last stroke. */
  readonly cut?: string;
}

export class BeatFadeDirector {
  readonly #fadeOverlay = new FadeOverlay();
  readonly #cues: BeatFadeCues;

  constructor(cues: BeatFadeCues) {
    this.#cues = cues;
  }

  /** What the hand-over waits on before it navigates. See `main.ts`. */
  get fade(): number {
    return this.#fadeOverlay.value;
  }

  handle(cue: { id: string }): void {
    if (cue.id === this.#cues.open) this.#fadeOverlay.setTarget(0);
    if (cue.id === this.#cues.fadeOut) this.#fadeOverlay.setTarget(1);
    if (this.#cues.cut !== undefined && cue.id === this.#cues.cut) this.#fadeOverlay.cutTo(1);
  }

  /**
   * On the RENDER step, with the render delta.
   *
   * `docs/traps.md`: *"A director needs `update(frameDt)` wired as well as
   * `step(dt)`."* Without this call the overlay never interpolates, and since it
   * exists at full black from the frame it is constructed, the beat is a black
   * screen that nothing in the console complains about.
   */
  update(frameDt: number): void {
    this.#fadeOverlay.update(frameDt);
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
  }
}
